import React, {
    useCallback, useEffect, useMemo, useState,
} from "react";
import styled from "@emotion/styled";
import { OperatingArea } from "@optimusride/client-library/@types/grpc/site";
import { useDispatch, useSelector } from "react-redux";
import { Coordinates } from "@optimusride/client-library/@types/grpc/common";
import { useHistory } from "react-router-dom";
import { DriveGear } from "@optimusride/client-library/@types/grpc/vehicle_status";
import { RootState } from "../../store";
import {
    SPACING_20, SPACING_60,
} from "../common/styles/spacing";
import { convertLatLng, getInitials } from "../common/utils";
import GoogleMaps from "../GoogleMaps";
import StationList from "./components/StationList";
import VehicleStatus from "./components/VehicleStatus";
import { createMapOptions } from "./utils";
import useStationMarker from "./hooks/useStationMarker";
import useVehicleMarker from "./hooks/useVehicleMarker";
import { CLOSED_LOOP_ROUTES } from "./assets/ClosedLoopRoutes";
import { setSelectedSite, UserRoles } from "../User/userSlice";
import { useQuery } from "../common/hooks/useQuery";
import { ROUTES } from "../Routes/utils/constants";

import { useVehicleRoute } from "./hooks/useVehicleRoute";

const StyledHome = styled.div`
    width: 100%;
    height: calc(100vh - ${SPACING_60});

    ${(props: { hasVehicles: boolean }) => (props.hasVehicles
        ? ` display: grid;
                    grid-template-areas: "Maps Station" "Maps Vehicle";
                    grid-template-columns: 60% 1fr;
                    grid-template-rows: 50% 1fr;`
        : ` display: grid;
                    grid-template-areas: "Maps Station";
                    grid-template-columns: 60% 1fr;
                    grid-template-rows: 100%;`)}

    overflow: hidden;
`;

const StyledGoogleMaps = styled(GoogleMaps)`
    height: 100%;
    width: 100%;
    transform: none;

    grid-area: Maps;
`;

const StyledStationList = styled(StationList)`
    grid-area: Station;

    margin: ${SPACING_20};
`;

const StyledVehicleStatus = styled(VehicleStatus)`
    grid-area: Vehicle;

    height: 37vh;

    margin: ${SPACING_20} ${SPACING_20} 0 ${SPACING_20};
`;

export default () => {
    const [center, setCenter] = useState<google.maps.LatLngLiteral>();

    const role = useSelector<RootState, UserRoles | undefined>(
        (state) => state.user.user?.commandorRole,
    );
    const selectedSite = useSelector<
        RootState,
        OperatingArea | undefined
    >((state) => state.user.siteDetails.selectedSite);
    const siteOptions = useSelector<
        RootState,
        OperatingArea[] | undefined
    >((state) => state.user.siteDetails.siteOptions);

    const history = useHistory();

    const dispatch = useDispatch();
    const query = useQuery();

    const vehicleVisibility = useSelector<
        RootState,
        { [keys: string]: boolean | undefined }
    >((state) => state.home.vehicleVisibility);

    const {
        stationList,
        stationLoading,
        stationMarkers,
        stationMarkersClusterDetails,
    } = useStationMarker(selectedSite);

    const {
        hasVehicles,
        movingMarkers,
        movingMarkerClusterDetails,
        filteredVehiclesWithStatus,
    } = useVehicleMarker(selectedSite);

    // Pass in the vehicle coordinate, status and id
    // to get the respective route data for that vehicle
    const routeInfo = useVehicleRoute(filteredVehiclesWithStatus
        .map(({ vehicle_id, coordinates, status }) => ({
            coordinates,
            vehicle_id,
            driveGear: status?.driveGear === undefined ? DriveGear.UNKNOWN : status.driveGear,
        })));

    // We memoized the options since this actually triggers the map to recreate itself
    // on each Home render which causes the markers to animate again
    const googleMapOptions = useMemo(() => createMapOptions(selectedSite), [selectedSite]);
    const staticRouteInfo = useMemo(
        () => CLOSED_LOOP_ROUTES[selectedSite?.id || -1] || [],
        [selectedSite],
    );

    // Checks if we are on a loop route, if so don't display the routes,
    // other wise display the individual routes
    const polyLines = useMemo(() => (staticRouteInfo.length > 1
        ? []
        : routeInfo.map(({ vehicleID, ...info }) => ({
            id: vehicleID,
            visible: !(vehicleVisibility[vehicleID] === false),
            ...info,
        }))), [routeInfo, vehicleVisibility]);

    const itemClickHandler = useCallback(
        (newCenter: Coordinates) => setCenter(convertLatLng(newCenter)),
        [setCenter],
    );

    // The flow of the query params goes
    // url updated => hits home => checks if valid => updates state => updates ui
    //                             if invalid => updates url to default site => valid route
    useEffect(() => {
        // finds the site that corresponds to the site query param
        const newSelectedSite = (siteOptions || [])?.find(({ name }) => getInitials(name) === query.get("site"));

        // if the site exists then we update state
        if (newSelectedSite) {
            if (newSelectedSite.id !== selectedSite?.id) {
                dispatch(setSelectedSite(newSelectedSite.id));
            }
        // if it does not exist we update the url
        // to the default site/ first site the user has access to
        } else if (siteOptions && siteOptions.length > 0) {
            history.replace(`${ROUTES.HOME}?site=${getInitials(siteOptions[0].name)}`);
        }
    }, [query]);

    useEffect(() => {
        setCenter(
            selectedSite?.latLonCenter
                ? convertLatLng(selectedSite.latLonCenter)
                : undefined,
        );
    }, [selectedSite]);

    return (
        <StyledHome hasVehicles={hasVehicles}>
            <StyledGoogleMaps
                loading={!(stationLoading || selectedSite)}
                markers={stationMarkers}
                markersClusterDetails={stationMarkersClusterDetails}
                movingMarkerClusterDetails={movingMarkerClusterDetails}
                center={center}
                options={googleMapOptions}
                movingMarkers={movingMarkers}
                staticRouteInfo={staticRouteInfo}
                polyLines={polyLines}
            />
            <StyledStationList
                operatingArea={selectedSite?.id || -1}
                stations={stationList}
                isAdmin={role === "admin"}
                onClick={itemClickHandler}
            />
            {hasVehicles && (
                <StyledVehicleStatus
                    vehicleStatus={filteredVehiclesWithStatus}
                    onClick={itemClickHandler}
                />
            )}
        </StyledHome>
    );
};
